import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, History, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import EquipmentHistoryPanel from '../EquipmentHistory/EquipmentHistoryPanel';
import styles from './StatisticsDetailModal.module.css';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const StatisticsDetailModal = ({ detail, queryString, onClose }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ count: 0, results: [], has_more: false });
  const [loading, setLoading] = useState(false);
  const [historyEquipmentId, setHistoryEquipmentId] = useState(null);

  const requestKey = useMemo(() => JSON.stringify(detail || {}), [detail]);

  useEffect(() => {
    if (!detail) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [detail]);

  useEffect(() => {
    setSearch('');
    setPage(1);
    setData({ count: 0, results: [], has_more: false });
    setHistoryEquipmentId(null);
  }, [requestKey]);

  useEffect(() => {
    if (!detail) return undefined;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams(queryString || '');
        params.set('metric', detail.metric || 'in_service');
        if (detail.value) params.set('value', detail.value);
        if (detail.projectId) params.set('detail_project_id', detail.projectId);
        if (search.trim()) params.set('search', search.trim());
        params.set('page', String(page));
        params.set('page_size', '100');
        const response = await api.get(`/stats/service-overview/details/?${params.toString()}`);
        setData(previous => ({
          ...response.data,
          results: page === 1
            ? (response.data.results || [])
            : [...previous.results, ...(response.data.results || [])],
        }));
      } catch (error) {
        toast.error(error.response?.data?.error || 'Не удалось загрузить детализацию');
      } finally {
        setLoading(false);
      }
    }, search ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [detail, page, queryString, search]);

  if (!detail) return null;

  const openRequest = (requestId) => {
    onClose();
    navigate(`/repair-requests/${requestId}`);
  };
  const openEquipmentHistory = (equipmentId) => setHistoryEquipmentId(equipmentId);

  return (
    <div className={styles.overlay} role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="statistics-detail-title">
        <header className={styles.header}>
          <div>
            <h2 id="statistics-detail-title">{detail.title}</h2>
            <p>{detail.subtitle || 'Конкретное оборудование, вошедшее в показатель'}</p>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Закрыть"><X size={22} /></button>
        </header>

        <div className={styles.toolbar}>
          <div className={styles.searchBox}><Search size={18} /><input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Заявка, серийный номер, модель или заказчик…" /></div>
          <strong>{data.count.toLocaleString('ru-RU')} аппаратов</strong>
        </div>

        <div className={styles.body}>
          {loading && page === 1 ? <div className={styles.state}>Загрузка детализации…</div> : !data.results.length ? <div className={styles.state}>По выбранному показателю оборудование не найдено</div> : (
            <div className={styles.tableWrap}>
              <table>
                <thead><tr><th>Заявка</th><th>Серийный номер</th><th>Модель</th><th>Заказчик / город</th><th>Этап</th><th>Ремонт</th><th>Корзина</th><th>Возраст</th><th>Дата заявки</th></tr></thead>
                <tbody>{data.results.map(item => (
                  <tr key={item.id}>
                    <td><button type="button" className={styles.linkButton} onClick={() => openRequest(item.request_id)}>{item.request_number}<ExternalLink size={13} /></button></td>
                    <td><button type="button" className={styles.serialButton} onClick={() => openEquipmentHistory(item.id)} title="Открыть историю оборудования">{item.serial_number}<History size={14} /></button></td>
                    <td title={item.equipment_model}>{item.equipment_model || '—'}</td>
                    <td><span>{item.customer || '—'}</span><small>{item.city || item.service_center || '—'}</small></td>
                    <td><span className={styles.status}>{item.location_status_display}</span></td>
                    <td>{item.repair_status_display || '—'}</td>
                    <td>{item.basket || '—'}</td>
                    <td><strong>{item.age_days}</strong> дн.</td>
                    <td>{formatDate(item.request_created_at)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {data.has_more && <button type="button" className={styles.moreButton} onClick={() => setPage(value => value + 1)} disabled={loading}>{loading ? 'Загрузка…' : `Показать ещё (${data.results.length} из ${data.count})`}</button>}
        </div>
      </section>
      <EquipmentHistoryPanel
        isOpen={Boolean(historyEquipmentId)}
        equipmentId={historyEquipmentId}
        onClose={() => setHistoryEquipmentId(null)}
        title="История оборудования"
      />
    </div>
  );
};

export default StatisticsDetailModal;
